import { EventEmitterInterface } from 'pf-frontend-common/src/module/event/emitter.interface';
import { StatsContextLocalInterface } from 'pf-frontend-common/src/module/stats/context/local.interface';
import { WindowLocationServiceInterface } from 'pf-frontend-common/src/service/window-location/service.interface';

import { PropertyModel } from 'common/data/property/model';
import { PropertySavedModel } from 'common/data/property-saved/model';
import { PropertySavedStoreEvent } from 'common/data/property-saved/store.event';
import { UserLoginStatsGtmAfterSavePopupImpression } from 'common/data/user/login/stats/gtm/after-save-popup-impression';
import { arrayFind } from 'common/helper/array/find';
import { configGetEnv } from 'common/helper/config/get-env';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { DataStore } from 'common/module/data/store';
import { MessageViewStore } from 'common/module/message/view-store';
import { MyAccountToggleSaveButtonInteractionEvent } from 'common/module/my-account/toggle-save-button/interaction.event';
import { MyAccountToggleSaveButtonInteractionPayloadInterface } from 'common/module/my-account/toggle-save-button/interaction/payload.interface';
import { MyAccountToggleSaveButtonListingContextEnum } from 'common/module/my-account/toggle-save-button/listing-context.enum';
import { MyAccountToggleSaveButtonLoginButton } from 'common/module/my-account/toggle-save-button/login-button';
import { MyAccountToggleSaveButtonManageButton } from 'common/module/my-account/toggle-save-button/manage-button';
import { MyAccountToggleSaveButtonTemplatePropsInterface } from 'common/module/my-account/toggle-save-button/template-props.interface';
import { MyAccountToggleSaveButtonViewStoreOptionsInterface } from 'common/module/my-account/toggle-save-button/view-store/options.interface';
import { PropertyListViewStoreEvent } from 'common/module/property/list/view-store.event';
import { PropertySavedStatechartStore } from 'common/module/property/saved/statechart/store';
import { SaveButtonSyncPayloadInterface } from 'common/module/save-button/sync-payload.interface';
import { StatsGtmProviderInterface } from 'common/module/stats/gtm/provider.interface';
import { StatsTealiumConversionEvent } from 'common/module/stats/tealium/conversion-event';
import { StatsTealiumDataLayerEventActionEnum } from 'common/module/stats/tealium/data-layer/event-action.enum';
import { StatsTealiumDataLayerEventCategoryEnum } from 'common/module/stats/tealium/data-layer/event-category.enum';
import { StatsTealiumDataLayerEventLabelEnum } from 'common/module/stats/tealium/data-layer/event-label.enum';
import { StatsTealiumDataLayerEventReferenceEnum } from 'common/module/stats/tealium/data-layer/event-reference.enum';
import { StatsTealiumDataLayerTealiumEventEnum } from 'common/module/stats/tealium/data-layer/tealium-event.enum';
import { StatsTealiumProviderInterface } from 'common/module/stats/tealium/provider.interface';
import { UserAuthTracker } from 'common/module/user/auth/tracker';
import { PropertySavedStoreServiceInterface } from 'common/service/property-saved-store/service.interface';
import { UserAuthenticationServiceInterface } from 'common/service/user-authentication/service.interface';

import { MyAccountToggleSaveButtonActionEvent } from './action.event';
import { MyAccountToggleSaveButtonInteractionSourceEnum } from './interaction-source.enum';

export class MyAccountToggleSaveButtonViewStore extends DataStore {
  /**
   * @inheritDoc
   */
  protected state: MyAccountToggleSaveButtonTemplatePropsInterface;

  /**
   * Stats local context
   */
  private statsContextLocal: StatsContextLocalInterface = {};

  /**
   * Constructor
   */
  constructor(
    eventEmitter: EventEmitterInterface,
    private propertySavedStatechartStore: PropertySavedStatechartStore,
    private propertySavedStoreService: PropertySavedStoreServiceInterface,
    private userAuthenticationService: UserAuthenticationServiceInterface,
    private toggleSaveButtonInteractionService: EventEmitterInterface,
    private statsGtm: StatsGtmProviderInterface,
    private statsTealium: StatsTealiumProviderInterface,
    private messageService: MessageViewStore,
    private windowLocationService: WindowLocationServiceInterface,
    private userAuthTracker: UserAuthTracker
  ) {
    super(eventEmitter);
  }

  /**
   * @inheritDoc
   */
  public initialize(options: MyAccountToggleSaveButtonViewStoreOptionsInterface): void {
    this.propertySavedStatechartStore.initialize();
    this.updateState(options);

    this.observers();
  }

  /**
   * @inheritdoc
   */
  public destroy(): void {
    this.propertySavedStatechartStore.destroy();

    this.propertySavedStoreService
      .getEventEmitter()
      .removeListener(PropertySavedStoreEvent.sync, this.onSyncPropertySavedStore);
  }

  /**
   * Initialize state
   */
  public updateState(options: MyAccountToggleSaveButtonViewStoreOptionsInterface): void {
    this.propertySavedStatechartStore.reset();
    this.statsContextLocal = {
      ...this.statsContextLocal,
      ...options.statsContext,
    };
    this.state = {
      id: options.id,
      property: options.property,
      isSmartad: options.isSmartad || false,
      isCts: options.isCts || false,
      displayCompleteProfileScreen: this.isFirstSavedProperty(),
      displayAlternativeCompleteProfileScreen: this.displayAlternativeCompleteProfileScreen(),
      isUserLoggedIn: this.isUserLoggedIn(),
      userHasEmail: this.userHasEmail(),
      sendTealiumEvent: this.sendTealiumEvent,
      isSaved: this.isSaved(options.property),
      isAfterSavePopupVisible: !!(this.state && this.state.isAfterSavePopupVisible),
      isNeedToSaveAfterLogin: !!(this.state && this.state.isNeedToSaveAfterLogin),
      onCloseAfterSavePopup: this.onCloseAfterSavePopup,
      listingContext: options.listingContext || MyAccountToggleSaveButtonListingContextEnum.default,
      onClickSave: this.onClickSave,
      onClickUnsave: this.onClickUnsave,
      className: options.className,
      isSaveMessageShownAfterPopup: options.isSaveMessageShownAfterPopup,
      targetProperty: this.state?.targetProperty,
      targetInteractionSource: this.state?.targetInteractionSource,
    };
  }

  /**
   * @inheritDoc
   */
  public getState(): MyAccountToggleSaveButtonTemplatePropsInterface {
    return this.state;
  }

  /**
   * Return true if user is logged in
   */
  public isUserLoggedIn(): boolean {
    return !!this.userAuthenticationService?.getToken();
  }

  public userHasEmail(): boolean {
    return !!this.userAuthenticationService.getUser()?.email;
  }

  private displayAlternativeCompleteProfileScreen(): boolean {
    return this.isUserLoggedIn() && !this.userAuthenticationService.getUser()?.email;
  }

  private sendTealiumEvent = (type: 'open' | 'continue' | 'skip' | 'submit'): void => {
    switch (type) {
      case 'open':
        this.onReceiveUpdatesModalOpen();
        break;
      case 'continue':
        this.onReceiveUpdatesModalContinue();
        break;
      case 'skip':
        this.onReceiveUpdatesModalSkip();
        break;
      case 'submit':
        this.onReceiveUpdatesModalSubmit();
        break;
    }
  };

  private onReceiveUpdatesModalOpen(): void {
    this.statsTealium.send({
      event_category: StatsTealiumDataLayerEventCategoryEnum.savedProperties,
      event_action: StatsTealiumDataLayerEventActionEnum.savePropertyNotificationModalOpen,
      tealium_event: StatsTealiumDataLayerTealiumEventEnum.savePropertyNotificationModalOpen,
      event_label: '',
      sendToGa: true,
    });
  }

  private onReceiveUpdatesModalContinue(): void {
    this.statsTealium.send({
      event_category: StatsTealiumDataLayerEventCategoryEnum.savedProperties,
      event_action: StatsTealiumDataLayerEventActionEnum.savePropertyKeepPostedClick,
      tealium_event: StatsTealiumDataLayerTealiumEventEnum.savePropertyKeepPostedClick,
      event_label: '',
      sendToGa: true,
    });
  }

  private onReceiveUpdatesModalSkip(): void {
    this.statsTealium.send({
      event_category: StatsTealiumDataLayerEventCategoryEnum.savedProperties,
      event_action: StatsTealiumDataLayerEventActionEnum.savePropertyKeepPostedSkipped,
      tealium_event: StatsTealiumDataLayerTealiumEventEnum.savePropertyKeepPostedSkipped,
      event_label: '',
      sendToGa: true,
    });
  }

  private onReceiveUpdatesModalSubmit(): void {
    this.statsTealium.send({
      user_email: this.userAuthenticationService.getUser()?.email,
      event_category: StatsTealiumDataLayerEventCategoryEnum.savedProperties,
      event_action: StatsTealiumDataLayerEventActionEnum.savePropertyEmailSubmit,
      tealium_event: StatsTealiumDataLayerTealiumEventEnum.savePropertyEmailSubmit,
      event_label: '',
      sendToGa: true,
    });
  }

  private isFirstSavedProperty(): boolean {
    const savedPropertyCount = this.propertySavedStoreService.getAllFromLocalStorage();
    return this.isUserLoggedIn() && !this.userAuthenticationService.getUser()?.email && savedPropertyCount.length === 0;
  }
  /**
   * Check if is saved
   */
  private isSaved(propertyModel: PropertyModel): boolean {
    return !!arrayFind(
      this.propertySavedStoreService.getAllFromLocalStorage(),
      (model) => model.property_id === parseInt(propertyModel.id, 10)
    );
  }

  /**
   * Attach observers
   */
  private observers(): void {
    // Saved property store
    this.propertySavedStoreService
      .getEventEmitter()
      .addListener(PropertySavedStoreEvent.sync, this.onSyncPropertySavedStore);
  }

  /**
   * Synced saved properties store
   */
  private onSyncPropertySavedStore = (savedProperties: PropertySavedModel[]) => {
    this.state.isSaved = this.isSaved(this.state.property);

    const savedPropertiesIds = savedProperties.reduce<SaveButtonSyncPayloadInterface>((result, current) => {
      result[String(current.property_id)] = true;
      return result;
    }, {});

    this.setState(this.state);
    this.getEventEmitter().emit(MyAccountToggleSaveButtonActionEvent.sync, savedPropertiesIds, this.state.property);
  };

  /**
   * After save popup has been closed
   */
  private onCloseAfterSavePopup = () => {
    this.state.isAfterSavePopupVisible = false;
    this.getEventEmitter().emit(MyAccountToggleSaveButtonInteractionEvent.closeSignInPopup);
    this.setState(this.state);

    if (this.state.isNeedToSaveAfterLogin) {
      const token = this.userAuthenticationService.getToken();
      if (token) {
        this.state = {
          ...this.state,
          isUserLoggedIn: true,
        };
        this.setState(this.state);
        this.saveProperty();
        this.messageService.alert({
          message: i18nTranslate('carousel-nudge:saved'),
          action: MyAccountToggleSaveButtonManageButton({
            onClick: this.onClickManageButton,
          }),
        });
      } else {
        this.messageService.alert({
          message: i18nTranslate('property-could-not-saved'),
          action: MyAccountToggleSaveButtonLoginButton({
            onClick: this.onClickLoginButton,
          }),
        });
      }
    } else if (this.state.isSaved && this.state.isSaveMessageShownAfterPopup) {
      this.messageService.alert({
        message: i18nTranslate('carousel-nudge:saved'),
        action: MyAccountToggleSaveButtonManageButton({
          onClick: this.onClickManageButton,
        }),
      });
    }
  };

  private saveProperty = () => {
    const propertyModel = this.state.targetProperty;
    const interactionSource = this.state.targetInteractionSource;
    this.propertySavedStatechartStore.save(propertyModel.id);

    // Log stats
    const context: StatsContextLocalInterface = {
      ...this.statsContextLocal,
    };

    const payload: MyAccountToggleSaveButtonInteractionPayloadInterface = {
      property: propertyModel,
      statsContext: context,
      listingContext: this.state.listingContext,
    };

    this.toggleSaveButtonInteractionService.emit(MyAccountToggleSaveButtonInteractionEvent.save, payload);

    this.state.isSaved = this.isSaved(this.state.property);

    this.setState(this.state);

    this.statsTealium.send(
      StatsTealiumConversionEvent(StatsTealiumDataLayerTealiumEventEnum.savedPropertyAdd, propertyModel, {
        sendToGa: true,
        eventAction: StatsTealiumDataLayerEventActionEnum.savedPropertyAdd,
        eventCategory: StatsTealiumDataLayerEventCategoryEnum.savedProperties,
        eventLabel: interactionSource as unknown as StatsTealiumDataLayerEventLabelEnum,
      })
    );
  };

  /**
   * Clicked save button
   */
  private onClickSave = (
    propertyModel: PropertyModel,
    interactionSource?: MyAccountToggleSaveButtonInteractionSourceEnum
  ) => {
    this.state.isNeedToSaveAfterLogin = false;
    this.state.targetProperty = propertyModel;
    this.state.targetInteractionSource = interactionSource;
    this.setState(this.state);

    const token = this.userAuthenticationService.getToken();
    if (!token) {
      this.userAuthTracker.onLoginModalOpen(StatsTealiumDataLayerEventReferenceEnum.SAVED_PROPERTIES);
      this.state.isAfterSavePopupVisible = true;
      this.state.isNeedToSaveAfterLogin = true;
      this.setState(this.state);
      this.statsGtm.send(UserLoginStatsGtmAfterSavePopupImpression);
      this.getEventEmitter().emit(MyAccountToggleSaveButtonInteractionEvent.openSignInPopup);
      this.getEventEmitter().emit(MyAccountToggleSaveButtonActionEvent.sync, this.state.property);
      return;
    } else {
      this.saveProperty();
      this.messageService.alert({
        message: i18nTranslate('carousel-nudge:saved'),
        action: MyAccountToggleSaveButtonManageButton({
          onClick: this.onClickManageButton,
        }),
      });
    }
  };

  /**
   * Clicked unsave button
   */
  private onClickUnsave = (
    propertyModel: PropertyModel,
    interactionSource?: MyAccountToggleSaveButtonInteractionSourceEnum
  ) => {
    // Unsave property
    this.propertySavedStatechartStore.unsave(propertyModel.id);

    this.messageService.alert({
      message: i18nTranslate('property-unsaved'),
    });

    // Log stats
    const context: StatsContextLocalInterface = {
      ...this.statsContextLocal,
    };

    const payload: MyAccountToggleSaveButtonInteractionPayloadInterface = {
      property: propertyModel,
      statsContext: context,
      listingContext: this.state.listingContext,
    };

    this.toggleSaveButtonInteractionService.emit(MyAccountToggleSaveButtonInteractionEvent.unsave, payload);

    this.getEventEmitter().emit(PropertyListViewStoreEvent.unsaveProperty, propertyModel);

    this.state.isSaved = this.isSaved(this.state.property);

    this.setState(this.state);

    this.statsTealium.send(
      StatsTealiumConversionEvent(StatsTealiumDataLayerTealiumEventEnum.savedPropertyRemove, propertyModel, {
        sendToGa: true,
        eventAction: StatsTealiumDataLayerEventActionEnum.savedPropertyRemove,
        eventCategory: StatsTealiumDataLayerEventCategoryEnum.savedProperties,
        eventLabel: interactionSource as unknown as StatsTealiumDataLayerEventLabelEnum,
      })
    );
  };

  /**
   * Redirect to the user's saved property page
   */
  private onClickManageButton = () => {
    this.windowLocationService.redirect(configGetEnv().userAccountSavedPropertiesUrl, {
      hash: '',
      queryData: {},
    });
  };

  /**
   * Shows the login prompt again
   */
  private onClickLoginButton = () => {
    this.state.isNeedToSaveAfterLogin = true;
    this.state.isAfterSavePopupVisible = true;
    this.getEventEmitter().emit(MyAccountToggleSaveButtonInteractionEvent.openSignInPopup);
    this.userAuthTracker.onLoginModalOpen(StatsTealiumDataLayerEventReferenceEnum.SAVED_PROPERTIES);
    this.setState(this.state);
  };
}
